// @lat: [[tasks#SEO Tasks#Frontend#Map plan]]
// What the Query Map recommends, shared by the map's cards and the Tasks page's Map plan tab.

// The topic's next move (Jev's topic_judgments.NEXT_MOVES, same labels as the Search topics tab)
// decides the card's one action: write an article, rework the page's copy, or fix structure.
export const MOVES = {
  write_new:      { label: "Write new page",     color: "#DC2626", action: "write" },
  expand_page:    { label: "Expand page",        color: "#D97706", action: "copy" },
  fix_snippet:    { label: "Fix title & meta",   color: "#D97706", action: "copy" },
  internal_links: { label: "Add internal links", color: "#2563EB", action: "links" },
  consolidate:    { label: "Merge pages",        color: "#7C3AED", action: "merge" },
  maintain:       { label: "Keep fresh",         color: "#059669", action: null },
  deprioritize:   { label: "Deprioritize",       color: "#9CA3AF", action: null },
};

// Lead potential from the topic view (query_topics.lead_tier: buyer fit + awareness, not volume),
// same labels as the Search topics tab.
export const TIERS = {
  high:     { label: "High priority",   bg: "#018D4C", text: "#FFFFFF" },
  medium:   { label: "Medium priority", bg: "#E4E4E7", text: "#27272A" },
  low:      { label: "Low priority",    bg: "#F4F4F5", text: "#A1A1AA" },
  unscored: { label: "Not scored",      bg: "#FFFFFF", text: "#A1A1AA" },
};

// Before Jev has judged a topic, coverage stands in (same fallback as Recommendations):
// nothing answers it → write; a page answers it → improve that page.
export function topicMove(t) {
  if (t.next_move) return t.next_move;
  return t.matched_cluster_id || t.matched_key_page ? "expand_page" : "write_new";
}

const mapKey = (s) => (s || "").trim().toLowerCase();

// Map move → seo_tasks.action_type.
const TASK_ACTION = {
  write_new: "write_new",
  expand_page: "optimize",
  fix_snippet: "optimize",
  internal_links: "internal_links",
  consolidate: "fix_cannibalization",
};
const TIER_PRIORITY = { high: "high", medium: "medium", low: "low", unscored: "low" };
const TIER_RANK = { high: 0, medium: 1, low: 2, unscored: 3 };
// Within a tier: a missing page loses the most leads, overlapping pages next, links last.
const MOVE_RANK = { write_new: 0, consolidate: 1, expand_page: 2, fix_snippet: 3, internal_links: 4 };
const QUERIES_PER_TASK = 10;

/** The Query Map's saved selection for this org (`sp:query-map:<orgId>`), or null if never set. */
export function readMapSelection(orgId) {
  try {
    const raw = localStorage.getItem(`sp:query-map:${orgId || "default"}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Ranked task recommendations for what's on the map: one per topic card with an actionable
 * next move, one per missing link. Ranked by lead tier, then move, then buyer fit, keeping the
 * topic view's order (tier, then business-weighted demand) for ties. Search volume never ranks.
 *
 * topics: GET /gsc/performance/query-topics rows. queries: gsc_queries rows (most valuable first).
 * selection: the map's saved state ({topics, bridges, focus}) or null → the map's default top 6.
 */
export function buildPlan({ topics, queries, selection, defaultTopics = 6 }) {
  const byTopic = new Map();
  queries.forEach((q) => {
    if (!q.query_topic_id) return;
    if (!byTopic.has(q.query_topic_id)) byTopic.set(q.query_topic_id, []);
    byTopic.get(q.query_topic_id).push(q);
  });
  byTopic.forEach((qs) => qs.sort((a, b) => (b.buyer_fit ?? -1) - (a.buyer_fit ?? -1)
    || (b.commercial_value ?? -1) - (a.commercial_value ?? -1)
    || (b.impressions || 0) - (a.impressions || 0)));

  const onMap = new Set(selection?.topics || topics.slice(0, defaultTopics).map((t) => mapKey(t.topic)));
  const order = new Map(topics.map((t, i) => [t.id, i]));
  const items = [];
  const skipped = [];

  topics.filter((t) => onMap.has(mapKey(t.topic))).forEach((t) => {
    const move = topicMove(t);
    if (!TASK_ACTION[move]) {
      skipped.push({ label: t.topic, move });
      return;
    }
    const qs = (byTopic.get(t.id) || []).slice(0, QUERIES_PER_TASK).map((q) => q.query);
    const tier = t.lead_tier || "unscored";
    items.push({
      id: `topic:${mapKey(t.topic)}`,
      kind: "topic",
      label: t.topic,
      move,
      tier,
      buyerFit: t.buyer_fit,
      impressions: t.impressions || 0,
      why: (t.lead_reasons || []).join(" · "),
      rank: [TIER_RANK[tier], MOVE_RANK[move], -(t.buyer_fit ?? -1), order.get(t.id)],
      task: {
        title: `${MOVES[move].label}: ${t.topic}`,
        action_type: TASK_ACTION[move],
        priority: TIER_PRIORITY[tier],
        target_url: move === "write_new" ? null : t.matched_key_page || null,
        labels: ["query-map"],
        queries: qs,
        description: [
          t.problem,
          `${TIERS[tier].label}${t.lead_reasons?.length ? ` — ${t.lead_reasons.join(", ")}` : ""}.`,
          `From the Query Map. ${t.queries || qs.length} searches, ${t.impressions || 0} impressions (feedback, not the goal).`,
        ].filter(Boolean).join("\n\n"),
      },
    });
  });

  (selection?.bridges || []).filter((b) => b.s && !b.pending).forEach((b) => {
    const s = b.s;
    const covered = s.covered_by;
    // An existing topic already holds the idea: link the two through it instead of writing.
    const move = covered ? "internal_links" : "write_new";
    items.push({
      id: `bridge:${b.id}`,
      kind: "bridge",
      label: covered ? `${b.aLabel} ↔ ${b.bLabel} via ${covered}` : s.title,
      move,
      tier: "medium",
      between: [b.aLabel, b.bLabel],
      concept: s.concept,
      why: s.why,
      impressions: 0,
      rank: [TIER_RANK.medium, MOVE_RANK[move], 0, Infinity],
      task: {
        title: covered ? `Link "${b.aLabel}" and "${b.bLabel}" via ${covered}` : `Missing link: ${s.title}`,
        action_type: TASK_ACTION[move],
        priority: "medium",
        target_url: null,
        labels: ["query-map", "missing-link"],
        // Not searched yet: the task picks up impressions once Search Console sees them.
        queries: [...(s.queries || []), ...(s.serves_a || []), ...(s.serves_b || [])].slice(0, QUERIES_PER_TASK),
        description: [
          `Missing link between "${b.aLabel}" and "${b.bLabel}"${s.concept ? `: ${s.concept}` : ""}.`,
          b.focus ? `Audience and situation: ${b.focus}.` : "",
          s.why,
          covered ? `"${covered}" already covers it: link both pages to and from it.` : `Link to and from the pages for "${b.aLabel}" and "${b.bLabel}".`,
        ].filter(Boolean).join("\n\n"),
      },
    });
  });

  const cmp = (a, b) => {
    for (let i = 0; i < a.rank.length; i++) {
      if (a.rank[i] !== b.rank[i]) return a.rank[i] < b.rank[i] ? -1 : 1;
    }
    return 0;
  };
  return { items: items.sort(cmp), skipped, fromDefault: !selection?.topics, focus: selection?.focus || "" };
}
